import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Grid,
  Paper,
  Typography,
  Collapse,
  Autocomplete
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import IconButton from '@mui/material/IconButton';
import DateRangePicker from './DateRangePicker';
import ClientAutocomplete from './ClientAutocomplete';

/**
 * TableFilters Component
 * Config-driven filter component for tables
 * Compact, dense filter bar with collapsible design
 */
const TableFilters = ({
  filters = [],
  filterValues = {},
  onFilterChange,
  statusOptions = [],
  employeeOptions = [],
  cityOptions = [],
  stateOptions = [],
  expanded: expandedProp,
  onExpandedChange,
}) => {
  const [localFilters, setLocalFilters] = useState(filterValues);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  useEffect(() => {
    setLocalFilters(filterValues);
  }, [filterValues]);

  const setExpanded = (value) => {
    if (isControlled && onExpandedChange) onExpandedChange(value);
    else setInternalExpanded(value);
  };

  const handleFilterChange = (filterId, value) => {
    const newFilters = { ...localFilters, [filterId]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClientSelect = (client) => {
    const newFilters = {
      ...localFilters,
      client_id: client?._id || '',
      client_id_label: client?.client_name || '',
    };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {};
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const renderFilterField = (filter) => {
    switch (filter.type) {
      case 'text':
        return (
          <TextField
            key={filter.id}
            label={filter.label}
            value={localFilters[filter.id] || ''}
            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
              },
            }}
          />
        );

      case 'clientAutocomplete':
        return (
          <Box key={filter.id} sx={{ width: '100%' }}>
            <ClientAutocomplete
              value={
                localFilters.client_id
                  ? { _id: localFilters.client_id, client_name: localFilters.client_id_label || '' }
                  : null
              }
              onSelect={handleClientSelect}
              placeholder={filter.label}
              size="small"
              sx={{ width: '100%' }}
              ListboxProps={{ sx: { maxHeight: 280 } }}
            />
          </Box>
        );

      case 'employeeAutocomplete':
        return (
          <Autocomplete
            key={filter.id}
            options={employeeOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name ?? '')}
            value={
              employeeOptions.find((o) => (typeof o === 'string' ? o : o?.id) === localFilters.employee) ?? null
            }
            onChange={(e, newValue) => handleFilterChange('employee', newValue ? (typeof newValue === 'string' ? newValue : newValue.id) : '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={filter.label}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
              />
            )}
            renderOption={(props, option) => {
              const optValue = typeof option === 'string' ? option : option?.id;
              const optLabel = typeof option === 'string' ? option : option?.name;
              return (
                <li {...props} key={optValue}>
                  {optLabel}
                </li>
              );
            }}
            sx={{ width: '100%' }}
            ListboxProps={{ sx: { maxHeight: 280 } }}
          />
        );

      case 'select':
        let options = filter.id === 'status' ? statusOptions : [];
        if (filter.id === 'city') options = cityOptions;
        if (filter.id === 'state') options = stateOptions;
        const useEmployeeOptions = filter.id === 'employee' && employeeOptions.length > 0;
        if (useEmployeeOptions) options = employeeOptions;
        return (
          <TextField
            key={filter.id}
            select
            label={filter.label}
            value={localFilters[filter.id] || ''}
            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
              },
            }}
          >
            <MenuItem value="">All</MenuItem>
            {(options || []).map((option) => {
              const optValue = typeof option === 'string' ? option : option.id;
              const optLabel = typeof option === 'string' ? option : option.name;
              return (
                <MenuItem key={optValue ?? optLabel} value={optValue}>
                  {optLabel}
                </MenuItem>
              );
            })}
          </TextField>
        );

      case 'dateRange':
        return (
          <Box key={filter.id} sx={{ width: '100%' }}>
            <DateRangePicker
              value={localFilters[filter.id] || { from: null, to: null }}
              onChange={(dateRange) => handleFilterChange(filter.id, dateRange)}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Paper 
      sx={{ 
        p: { xs: 1, sm: 1.5 }, 
        mb: { xs: 1.5, sm: 2 },
        borderRadius: { xs: 1, sm: 2 },
        boxShadow: { xs: 1, sm: 2 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: expanded ? 1.5 : 0,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ fontSize: { xs: '1rem', sm: '1.125rem' }, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
            Filters
          </Typography>
        </Box>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Grid container spacing={{ xs: 1, sm: 1.5 }}>
          {filters.map((filter) => (
            <Grid
              item
              xs={filter.type === 'dateRange' ? 12 : 12}
              sm={filter.type === 'dateRange' ? 12 : 6}
              md={filter.type === 'dateRange' ? 12 : 4}
              lg={filter.type === 'dateRange' ? 8 : 3}
              key={filter.id}
            >
              {renderFilterField(filter)}
            </Grid>
          ))}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <Button
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.8125rem' }}
              >
                Clear Filters
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Paper>
  );
};

export default TableFilters;
